import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { CaretLeft, ClipboardText, Target, Plus, Trash } from "@phosphor-icons/react";
import EvalCard from "@/components/EvalCard";
import LtaResults from "@/components/LtaResults";

const GROUP_COLOR = {
  "Game Style Competencies": "#dfff00",
  "Technical Skills": "#e3735e",
  "Player Qualities": "#16ec06",
};

export default function PlayerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [player, setPlayer] = useState(null);
  const [trend, setTrend] = useState(null);
  const [evals, setEvals] = useState(null);
  const [goals, setGoals] = useState(null);

  const isCoach = ["coach", "head_coach"].includes(user?.role);

  const refetchEvals = () => {
    api.get(`/term-evaluations`, { params: { player_id: id } }).then((r) => setEvals(r.data));
    api.get(`/term-evaluations/trend/${id}`).then((r) => setTrend(r.data));
  };

  useEffect(() => {
    api.get(`/players/${id}`).then((r) => setPlayer(r.data));
    api.get(`/term-evaluations/trend/${id}`).then((r) => setTrend(r.data));
    api.get(`/term-evaluations`, { params: { player_id: id } }).then((r) => setEvals(r.data));
    api.get(`/goals`, { params: { player_id: id } }).then((r) => setGoals(r.data));
  }, [id]);

  return (
    <div className="space-y-5" data-testid="player-detail">
      <Link to={user?.role === "parent" ? "/parent" : "/players"} className="btn-ghost inline-flex items-center gap-1.5 -ml-2" data-testid="back-to-players">
        <CaretLeft size={18} weight="bold" /> Back
      </Link>

      <div className="surface p-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-label">Player</div>
          <h1 className="font-heading uppercase text-3xl sm:text-4xl tracking-tight mt-1 break-words">{player?.name || "…"}</h1>
          {player?.coaches?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {player.coaches.map((c) => (
                <span key={c.id} className="text-xs px-2 py-1 bg-[#1a1a1a] border border-[#27272a] rounded-md text-zinc-300">
                  Coach: {c.name}
                </span>
              ))}
            </div>
          )}
        </div>
        {isCoach && (
          <Link to={`/players/${id}/evaluate`} className="btn-primary shrink-0" data-testid="evaluate-link">
            <ClipboardText size={16} weight="bold" /> Evaluate
          </Link>
        )}
      </div>

      <TrendChart trend={trend} />

      <EvalsByDate evals={evals} isCoach={isCoach} playerId={id} onUpdated={refetchEvals} />

      <GoalsSection goals={goals} setGoals={setGoals} playerId={id} isCoach={isCoach} />

      {player && user?.role !== "parent" && (
        <LtaResults
          playerId={id}
          lta_id={player.lta_id}
          canEdit={["coach", "head_coach", "super_admin"].includes(user?.role)}
          onLtaIdChange={(newId) => setPlayer({ ...player, lta_id: newId })}
        />
      )}
    </div>
  );
}

function fmtDate(d) {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return d;
  }
}

function EvalsByDate({ evals, isCoach, playerId, onUpdated }) {
  const [publishing, setPublishing] = useState(null);
  if (!evals) return null;
  if (evals.length === 0) {
    return (
      <div className="surface p-6" data-testid="evals-empty">
        <div className="font-heading text-xl">No evaluations yet.</div>
        <div className="text-sm text-zinc-400 mt-1">Coaches: tap Evaluate to record the first session.</div>
      </div>
    );
  }
  const grouped = {};
  evals.forEach((e) => {
    const d = e.evaluation_date || (e.created_at || "").slice(0, 10);
    grouped[d] = grouped[d] || [];
    grouped[d].push(e);
  });
  const dates = Object.keys(grouped).sort().reverse();

  const publish = async (d) => {
    setPublishing(d);
    try {
      await api.post("/term-evaluations/publish", {
        player_id: playerId,
        evaluation_date: d,
        base_url: window.location.origin,
      });
      onUpdated?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(null);
    }
  };

  return (
    <section data-testid="evals-by-date">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-heading uppercase text-xl tracking-tight">Evaluations</h2>
        <div className="font-label">{evals.length} entries</div>
      </div>
      <div className="space-y-3">
        {dates.map((d, i) => {
          const items = grouped[d];
          const drafts = items.filter((e) => e.status === "draft").length;
          return (
            <details key={d} className="surface p-4" open={i === 0} data-testid={`eval-date-group-${d}`}>
              <summary className="cursor-pointer flex items-center justify-between list-none gap-2 flex-wrap">
                <span className="font-heading uppercase tracking-tight text-lg">{fmtDate(d)}</span>
                <span className="flex items-center gap-2">
                  {drafts > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-[#e3735e]/15 text-[#e3735e] font-bold rounded uppercase tracking-wider" data-testid={`draft-count-${d}`}>
                      {drafts} draft{drafts !== 1 ? "s" : ""}
                    </span>
                  )}
                  <span className="font-label">{items.length} rated</span>
                </span>
              </summary>
              {isCoach && drafts > 0 && (
                <div className="mt-3 bg-[#e3735e]/10 border border-[#e3735e]/30 rounded-md p-3 flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-xs text-[#e3735e] font-semibold">Drafts are not visible to parents yet.</span>
                  <button
                    onClick={() => publish(d)}
                    disabled={publishing === d}
                    className="btn-primary text-xs"
                    data-testid={`publish-date-${d}`}
                  >
                    {publishing === d ? "Publishing…" : "Publish & notify parents"}
                  </button>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-2 mt-3">
                {items.map((e) => (
                  <EvalCard key={e.id} ev={e} dense testid={`date-eval-${e.id}`} onUpdated={onUpdated} />
                ))}
              </div>
            </details>
          );
        })}
      </div>
    </section>
  );
}

function TrendChart({ trend }) {
  const [mode, setMode] = React.useState("groups"); // groups | categories
  if (!trend) return <div className="skeleton h-64" />;
  if (trend.length === 0)
    return (
      <div className="surface p-8 text-center text-zinc-500 text-sm trend-card">
        No evaluations yet — published ratings will build the trend over time.
      </div>
    );

  // Build merged date-ordered timeline
  const allPoints = trend.flatMap((s) => s.points);
  const sortMap = {};
  allPoints.forEach((p) => { sortMap[p.label] = p.sort_key; });
  const labels = Array.from(new Set(allPoints.map((p) => p.label))).sort(
    (a, b) => sortMap[a] - sortMap[b]
  );

  // Series with at least 2 points (otherwise can't show a line)
  const multiPoint = trend.filter((s) => s.points.length >= 2);
  const singlePoint = trend.filter((s) => s.points.length < 2);

  // GROUP VIEW: average per (group, term)
  const groupOrder = ["Game Style Competencies", "Technical Skills", "Player Qualities"];
  const groupColors = {
    "Game Style Competencies": "#dfff00",
    "Technical Skills": "#e3735e",
    "Player Qualities": "#16ec06",
  };
  const groupSeries = groupOrder
    .map((gname) => {
      const inGroup = multiPoint.filter((s) => s.group_name === gname);
      if (inGroup.length === 0) return null;
      const points = labels.map((label) => {
        const vals = inGroup
          .map((s) => s.points.find((p) => p.label === label)?.rating)
          .filter((v) => typeof v === "number");
        if (vals.length === 0) return null;
        return {
          term_label: label,
          rating: Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10,
        };
      }).filter(Boolean);
      if (points.length < 2) return null;
      return { name: gname, color: groupColors[gname], points };
    })
    .filter(Boolean);

  // CATEGORY VIEW: unique color per category (12-color palette)
  const palette = [
    "#dfff00", "#e3735e", "#16ec06",
    "#06b6d4", "#f59e0b", "#a855f7",
    "#ec4899", "#38bdf8", "#fde047",
    "#fb923c", "#22d3ee", "#84cc16",
  ];
  const catSeries = multiPoint.map((s, i) => ({
    name: s.category_name,
    group: s.group_name,
    color: palette[i % palette.length],
    points: s.points.map((p) => ({ term_label: p.label, rating: p.rating })),
  }));

  const series = mode === "groups" ? groupSeries : catSeries;

  // Build chart data
  const data = labels.map((label) => {
    const row = { term: label };
    series.forEach((s) => {
      const pt = s.points.find((p) => p.term_label === label);
      if (pt) row[s.name] = pt.rating;
    });
    return row;
  });

  // Compute delta for each series (last - first non-null)
  const seriesWithDelta = series.map((s) => {
    const ratings = s.points.map((p) => p.rating);
    const delta = ratings.length >= 2
      ? Math.round((ratings[ratings.length - 1] - ratings[0]) * 10) / 10
      : 0;
    return { ...s, delta };
  });

  return (
    <div className="surface p-4 trend-card" data-testid="trend-chart">
      <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="font-label">Progression over time</div>
          <h2 className="font-heading uppercase text-xl tracking-tight">Trend</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500 mr-1 hidden sm:inline">Rating 1–5</span>
          <div className="inline-flex bg-[#09090b] border border-[#27272a] rounded-md p-0.5" data-testid="trend-mode-toggle">
            <button
              onClick={() => setMode("groups")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded transition-colors ${mode === "groups" ? "bg-[#dfff00] text-black" : "text-zinc-400 hover:text-white"}`}
              data-testid="mode-groups"
            >
              Groups
            </button>
            <button
              onClick={() => setMode("categories")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded transition-colors ${mode === "categories" ? "bg-[#dfff00] text-black" : "text-zinc-400 hover:text-white"}`}
              data-testid="mode-categories"
            >
              Categories
            </button>
          </div>
        </div>
      </div>

      <div style={{ width: "100%", height: 280 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 10, right: 16, left: -16, bottom: 0 }}>
            <CartesianGrid stroke="#27272a" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="term" tick={{ fontSize: 11 }} stroke="#52525b" />
            <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} stroke="#52525b" tick={{ fontSize: 11 }} width={28} />
            <Tooltip
              contentStyle={{ backgroundColor: "#121212", border: "1px solid #27272a", borderRadius: 6 }}
              labelStyle={{ color: "#a1a1aa", fontSize: 11, marginBottom: 4 }}
              itemStyle={{ fontSize: 12, padding: 0 }}
            />
            {seriesWithDelta.map((s) => (
              <Line
                key={s.name}
                type="monotone"
                dataKey={s.name}
                stroke={s.color}
                strokeWidth={mode === "groups" ? 3.5 : 2.5}
                dot={{ r: mode === "groups" ? 5 : 3, fill: s.color, strokeWidth: 0 }}
                activeDot={{ r: 6 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend with delta badges */}
      <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3" data-testid="trend-legend">
        {seriesWithDelta.map((s) => (
          <div key={s.name} className="flex items-center gap-1.5 text-xs text-zinc-300">
            <span className="w-3 h-1 rounded" style={{ background: s.color }} />
            <span>{s.name}</span>
            {s.delta !== 0 && (
              <span
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  s.delta > 0 ? "bg-[#16ec06]/15 text-[#16ec06]" : "bg-[#ff0026]/15 text-[#ff0026]"
                }`}
              >
                {s.delta > 0 ? "↑" : "↓"} {Math.abs(s.delta).toFixed(1)}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Footnote for hidden single-point series */}
      {singlePoint.length > 0 && (
        <div className="mt-3 text-xs text-zinc-500 border-t border-[#27272a] pt-3" data-testid="trend-footnote">
          <span className="font-label mr-2">Needs more data</span>
          {singlePoint.map((s, i) => (
            <span key={s.category_id}>
              {s.category_name}{i < singlePoint.length - 1 ? ", " : ""}
            </span>
          ))}
          <span className="text-zinc-600"> — only rated once, not yet on the chart.</span>
        </div>
      )}
    </div>
  );
}

function GoalsSection({ goals, setGoals, playerId, isCoach }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");

  const save = async () => {
    if (!title.trim()) return;
    const res = await api.post("/goals", { player_id: playerId, title, description: desc });
    setGoals([res.data, ...(goals || [])]);
    setOpen(false);
    setTitle(""); setDesc("");
  };

  return (
    <section data-testid="goals-section">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-heading uppercase text-xl tracking-tight">Goals</h2>
        {isCoach && !open && (
          <button onClick={() => setOpen(true)} className="btn-ghost text-sm" data-testid="new-goal-btn">
            <Plus size={14} weight="bold" /> Goal
          </button>
        )}
      </div>
      {open && (
        <div className="surface p-3 mb-2" data-testid="new-goal-form">
          <input className="input mb-2" placeholder="Goal title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="new-goal-title" />
          <textarea className="input mb-2" placeholder="Description" rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} />
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary flex-1" onClick={save} data-testid="new-goal-save">Save</button>
          </div>
        </div>
      )}
      {goals?.length === 0 && <div className="surface p-5 text-zinc-500 text-sm">No goals set yet.</div>}
      <div className="grid gap-2">
        {goals?.map((g) => (
          <div key={g.id} className="surface p-4" data-testid={`goal-${g.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="font-semibold flex items-center gap-1.5"><Target size={16} weight="bold" className="text-[#dfff00]" />{g.title}</div>
              {isCoach && (
                <button
                  onClick={async () => {
                    if (!window.confirm(`Delete the goal "${g.title}"? This can't be undone.`)) return;
                    await api.delete(`/goals/${g.id}`);
                    setGoals((goals || []).filter((x) => x.id !== g.id));
                  }}
                  className="btn-ghost text-[#ff0026] text-xs shrink-0 -mt-1 -mr-1"
                  data-testid={`delete-goal-${g.id}`}
                  title="Delete goal"
                >
                  <Trash size={14} weight="bold" />
                </button>
              )}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">
              Set by {g.set_by_name} • status {g.status}{g.target_date ? ` • by ${g.target_date}` : ""}
            </div>
            {g.description && <div className="text-sm text-zinc-300 mt-2">{g.description}</div>}
            {g.updates?.length > 0 && (
              <div className="mt-3 border-t border-[#27272a] pt-3 space-y-2">
                {g.updates.slice(0, 3).map((u) => (
                  <div key={u.id} className="text-xs text-zinc-400">
                    <span className="text-zinc-200 font-semibold">{u.coach_name}</span> — {u.note}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
